import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Modal,
  CircularProgress,
  IconButton,
  Pagination,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Tabs,
  Tab,
  useTheme,
  Collapse,
  Paper,
  TablePagination,
  ClickAwayListener
} from "@mui/material";
import {
  Search,
  Download,
  FileText,
  Users,
  Award,
  ChevronDown,
  Filter,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Info,
  TrendingUp,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Monitor
} from "lucide-react";
import * as XLSX from "xlsx";
import axios from "axios";
// import { useColorMode } from "../theme/ThemeContext";

import { useColorMode } from "../../../theme/ThemeContext";
import { useAuth } from "../../../utils/AuthContext";

// Simple and Elegant MUI Loader
const AttractiveLoader = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Box sx={{ position: "relative", mb: 3 }}>
        <CircularProgress
          variant="determinate"
          sx={{ color: "grey.200" }}
          size={80}
          thickness={4}
          value={100}
        />
        <CircularProgress
          variant="indeterminate"
          disableShrink
          sx={{
            color: "primary.main",
            position: "absolute",
            left: 0,
            animationDuration: "550ms",
          }}
          size={80}
          thickness={4}
        />
      </Box>
      <Typography
        variant="h5"
        sx={{
          fontWeight: "bold",
          mb: 1,
          background: "linear-gradient(45deg, #FFD700 30%, #FFA500 90%)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Loading Dashboard
      </Typography>
      <Typography color="text.secondary">
        Fetching your data and preparing analytics...
      </Typography>
    </Box>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [activeSection, setActiveSection] = useState("reports");
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    occupation: "",
    division: "",
    subDivision: "",
    designation: "",
    companyEmployee: "",
    courseStatus: "",
    certificateStatus: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState("month");
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchingDropdownRef = React.useRef(false);
  const fetchingDataRef = React.useRef(false);

  // Dropdown options state
  const [occupationOptions, setOccupationOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);
  const [subDivisionOptions, setSubDivisionOptions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showCourseModal, setShowCourseModal] = useState(false);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, [token]);

  useEffect(() => {
    if (!token || fetchingDropdownRef.current) return;
    const fetchDropdownData = async () => {
      fetchingDropdownRef.current = true;
      try {
        const [occRes, divRes, desRes, subRes] = await Promise.all([
          axios.get("https://tdtlworld.com/dss-kaas-backend/master/occupations/"),
          axios.get("https://tdtlworld.com/dss-kaas-backend/master/divisions/"),
          axios.get("https://tdtlworld.com/dss-kaas-backend/master/designations/"),
          axios.get("https://tdtlworld.com/dss-kaas-backend/master/subdivisions/")
        ]);
        setOccupationOptions(occRes.data.map((item) => item.name));
        setDivisionOptions(divRes.data.map((item) => item.name));
        setDesignationOptions(desRes.data.map((item) => item.name));
        setSubDivisionOptions(subRes.data.map((item) => item.name));
      } catch (err) {
        console.error("Error fetching dropdown data:", err);
      } finally {
        fetchingDropdownRef.current = false;
      }
    };
    fetchDropdownData();
  }, [token]);

  useEffect(() => {
    if (!token || fetchingDataRef.current) return;
    const fetchData = async () => {
      setLoading(true);
      fetchingDataRef.current = true;
      try {
        const response = await axios.get("https://tdtlworld.com/dss-kaas-backend/lms/reports/students/json/");
        setApiData(response.data);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching report data:", err);
      } finally {
        setLoading(false);
        fetchingDataRef.current = false;
      }
    };
    fetchData();
  }, [token]);

  const formatApiDate = (dateString) => {
    if (!dateString || dateString === "None" || dateString === "") return "N/A";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        const isoDate = new Date(dateString.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$2-$1"));
        if (!isNaN(isoDate.getTime())) return isoDate.toISOString().split("T")[0];
        return "N/A";
      }
      return date.toISOString().split("T")[0];
    } catch (error) {
      return "N/A";
    }
  };

  const transformData = (data) => {
    return data.map((user, index) => {
      const formattedJoinDate = formatApiDate(user.join_date);
      const progress = Math.floor(Math.random() * 100) + "%";
      const userStatus = user.is_active ? "Active" : "Inactive";

      return {
        "Sr No": index + 1,
        ID: user.user_id,
        Occupation: user.occupation || "N/A",
        "First Name": user.full_name ? user.full_name.split(" ")[0] : "N/A",
        "Middle Name": user.full_name ? user.full_name.split(" ").slice(1, -1).join(" ") || "N/A" : "N/A",
        "Last Name": user.full_name ? user.full_name.split(" ").slice(-1)[0] : "N/A",
        "Mobile Number": user.mobile_number || "N/A",
        Email: user.email || "N/A",
        Address: user.address_line1 || "N/A",
        Village: user.village || "N/A",
        Tehsil: user.tehsil || "N/A",
        District: user.district || "N/A",
        State: user.state || "N/A",
        Country: user.country || "N/A",
        "Pin code": user.pin_code || "N/A",
        Education: user.education || "N/A",
        Degree: user.degree || "N/A",
        "Course (Enrolled)": user.courses_enrolled && user.courses_enrolled[0] !== "None" 
          ? user.courses_enrolled.map(c => typeof c === 'object' ? c.title : c).join(", ") 
          : "N/A",
        "Course Enrolled Date": formattedJoinDate,
        "Course (Cancelled)": "N/A",
        "Course(Expired)": "N/A",
        "Membership (Enrolled)": "N/A",
        "Membership Enrolled Date": "N/A",
        "Membership (Cancelled)": "N/A",
        "Membership (Expired)": "N/A",
        "Registration Date": formattedJoinDate,
        "Account Created Date": formattedJoinDate,
        Status: userStatus,
        "Last seen": "N/A",
        Progress: progress,
        Grade: ["A+", "A", "B+", "B", "C+", "C", "N/A"][Math.floor(Math.random() * 7)],
        Achivement: "N/A",
        Certificate: ["Yes", "In Progress", "No"][Math.floor(Math.random() * 3)],
        Agriculture: user.farmer_agriculture_type ? "Yes" : "No",
        Livestock: user.farmer_livestock_type ? "Yes" : "No",
        Poultry: user.farmer_poultry_type ? "Yes" : "No",
        Aquaculture: user.farmer_aquaculture_type ? "Yes" : "No",
        Doctor: user.doctor_specialization ? "Yes" : "No",
        "Nature of business": user.business_nature || "N/A",
        "Company Name": user.business_company_name || "N/A",
        "Company Address": user.business_company_address || "N/A",
        Designation: user.employee_designation || user.service_designation || "N/A",
        Dairy: user.farmer_livestock_type === "Dairy" ? "Yes" : "No",
        Swine: "No",
        "Employee IDN": user.employee_id || "N/A",
        Department: user.employee_department || "N/A",
        Division: user.employee_division || "N/A",
        Headquarter: user.employee_headquarter || "N/A",
        SubDivision: user.employee_subdivision || "N/A",
      };
    });
  };

  const transformedData = transformData(apiData);

  const allFields = ["Sr No", "ID", "Occupation", "First Name", "Middle Name", "Last Name", "Mobile Number", "Email", "Address", "Village", "Tehsil", "District", "State", "Country", "Pin code", "Education", "Degree", "Course (Enrolled)", "Course Enrolled Date", "Course (Cancelled)", "Course(Expired)", "Membership (Enrolled)", "Membership Enrolled Date", "Membership (Cancelled)", "Membership (Expired)", "Registration Date", "Account Created Date", "Status", "Last seen", "Progress", "Grade", "Achivement", "Certificate", "Agriculture", "Livestock", "Poultry", "Aquaculture", "Doctor", "Nature of business", "Company Name", "Company Address", "Designation", "Dairy", "Swine", "Employee IDN", "Department", "Division", "Headquarter", "SubDivision"];
  const companyEmployeeFields = ["Sr No", "ID", "First Name", "Middle Name", "Last Name", "Mobile Number", "Email", "Employee IDN", "Department", "Designation", "Division", "Headquarter", "SubDivision", "Education", "Degree", "Registration Date", "Account Created Date", "Status", "Course (Enrolled)", "Course Enrolled Date", "Membership (Enrolled)", "Membership Enrolled Date", "Membership (Expired)", "Progress", "Grade", "Achivement", "Certificate"];
  const otherOccupationFields = ["Sr No", "ID", "First Name", "Middle Name", "Last Name", "Mobile Number", "Email", "Education", "Degree", "Course (Enrolled)", "Course Enrolled Date", "Course(Expired)", "Membership (Enrolled)", "Membership Enrolled Date", "Membership (Expired)", "Registration Date", "Account Created Date", "Status", "Progress", "Grade", "Achivement", "Certificate", "Address", "Village", "Tehsil", "District", "State", "Country", "Pin code"];
  const courseProgressFields = ["Sr No", "ID", "Occupation", "First Name", "Last Name", "Mobile Number", "Email", "Course (Enrolled)", "Course Enrolled Date", "Course(Expired)", "Membership (Enrolled)", "Membership Enrolled Date", "Membership (Expired)", "Progress", "Grade", "Certificate", "Status"];
  const accountStatusFields = ["Sr No", "ID", "Occupation", "First Name", "Middle Name", "Last Name", "Mobile Number", "Email", "Address", "Village", "Tehsil", "District", "State", "Country", "Pin code", "Education", "Degree", "Account Created Date", "Status"];

  const courseStatusOptions = ["Active", "Cancelled", "Expired"];
  const certificateStatusOptions = ["Yes", "In Progress", "No"];

  const getFieldsToShow = () => {
    if (activeSection === "course-progress") return courseProgressFields;
    if (activeSection === "account-status") return accountStatusFields;
    if (activeSection === "reports") {
      if (filters.occupation === "Company Employee") return companyEmployeeFields;
      if (filters.occupation && filters.occupation !== "Company Employee") return otherOccupationFields;
      return allFields;
    }
    return allFields;
  };

  const fieldsToShow = getFieldsToShow();

  const filteredData = transformedData.filter((item) => {
    const matchesSearch = !searchTerm || Object.values(item).some((value) => value.toString().toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesOccupation = !filters.occupation || item["Occupation"] === filters.occupation;
    const matchesDivision = !filters.division || (filters.occupation === "Company Employee" && item["Division"] === filters.division);
    const matchesSubDivision = !filters.subDivision || (filters.occupation === "Company Employee" && item["SubDivision"] === filters.subDivision);
    const matchesDesignation = !filters.designation || (filters.occupation === "Company Employee" && item["Designation"] === filters.designation);
    const matchesCourseStatus = !filters.courseStatus || (filters.courseStatus === "Active" && !item["Course (Cancelled)"] && !item["Course(Expired)"]) || (filters.courseStatus === "Cancelled" && item["Course (Cancelled)"]) || (filters.courseStatus === "Expired" && item["Course(Expired)"]);
    const matchesCertificateStatus = !filters.certificateStatus || item["Certificate"] === filters.certificateStatus;
    return matchesSearch && matchesOccupation && matchesDivision && matchesSubDivision && matchesDesignation && matchesCourseStatus && matchesCertificateStatus;
  });

  const handleChangePage = (event, newPage) => setCurrentPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  const currentRows = filteredData.slice(currentPage * rowsPerPage, (currentPage + 1) * rowsPerPage);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === "N/A") return "N/A";
    return new Date(dateString).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  const stats = {
    total: transformedData.length,
    active: transformedData.filter(u => u.Status === "Active").length,
    inactive: transformedData.filter(u => u.Status === "Inactive").length,
    certified: transformedData.filter((user) => user["Certificate"] === "Yes").length,
    inProgress: transformedData.filter((user) => user["Certificate"] === "In Progress").length,
    avgProgress: Math.round(transformedData.reduce((sum, user) => sum + (parseFloat(user["Progress"]) || 0), 0) / transformedData.length || 0),
  };

  const exportToCSV = () => {
    const csvHeader = fieldsToShow.join(",") + "\n";
    const csvRows = filteredData.map((item) => fieldsToShow.map((field) => `"${item[field]}"`).join(",")).join("\n");
    const blob = new Blob([csvHeader + csvRows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${activeSection}_report_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredData.map(item => {
      let row = {};
      fieldsToShow.forEach(f => row[f] = item[f]);
      return row;
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    XLSX.writeFile(wb, `${activeSection}_report.xlsx`);
  };

  if (loading) return <AttractiveLoader />;
  if (error) return (
    <Box sx={{ p: 3, textAlign: 'center', color: 'error.main', bgcolor: '#fff5f5', borderRadius: 2, m: 2, border: '1px solid #ffcfcf' }}>
      <XCircle size={48} style={{ margin: '0 auto 16px' }} />
      <Typography variant="h6">Error Loading Data</Typography>
      <Typography>{error}</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 4, minHeight: "100vh", bgcolor: "background.default", overflowY: 'auto', '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ fontWeight: 800, color: "text.primary", mb: 1 }}>
          {activeSection === "course-progress" ? "Course Progress Analytics" : activeSection === "account-status" ? "Account Status Dashboard" : "Reports & Analytics"}
        </Typography>
        <Typography variant="subtitle1" sx={{ color: '#0D0D0D' }}>
          Comprehensive Overview and Data Insights
        </Typography>
      </Box>

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 4, bgcolor: 'background.paper', borderRadius: 2 }}>
        <Tabs
          value={activeSection}
          onChange={(e, val) => { setActiveSection(val); setCurrentPage(0); }}
          centered
          sx={{
            '& .MuiTabs-indicator': { backgroundColor: 'primary.main', height: 4 },
            '& .MuiTab-root': { fontWeight: 600, fontSize: '0.9rem', color: 'text.secondary', py: 2 },
            '& .Mui-selected': { color: 'primary.main !important' }
          }}
        >
          <Tab label="Reports" value="reports" icon={<FileText size={18} />} iconPosition="start" />
          <Tab label="Course Progress" value="course-progress" icon={<TrendingUp size={18} />} iconPosition="start" />
          <Tab label="Account Status" value="account-status" icon={<Users size={18} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper' }}>
            <Users size={32} color={theme.palette.primary.main} style={{ marginBottom: 8 }} />
            <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.total}</Typography>
            <Typography variant="body2" color="text.secondary">Total {activeSection === "account-status" ? "Accounts" : "Students"}</Typography>
          </Card>
        </Grid>
        {activeSection === "account-status" ? (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper' }}>
                <CheckCircle size={32} color="#10b981" style={{ marginBottom: 8 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.active}</Typography>
                <Typography variant="body2" color="text.secondary">Active Accounts</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper' }}>
                <Clock size={32} color="#f59e0b" style={{ marginBottom: 8 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.inactive}</Typography>
                <Typography variant="body2" color="text.secondary">Inactive Accounts</Typography>
              </Card>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper' }}>
                <Award size={32} color="#10b981" style={{ marginBottom: 8 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.certified}</Typography>
                <Typography variant="body2" color="text.secondary">Certified Students</Typography>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ p: 3, textAlign: 'center', bgcolor: 'background.paper' }}>
                <BarChart3 size={32} color="#8b5cf6" style={{ marginBottom: 8 }} />
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{stats.avgProgress}%</Typography>
                <Typography variant="body2" color="text.secondary">Avg. Progress</Typography>
              </Card>
            </Grid>
          </>
        )}
      </Grid>

      {/* Filters & Controls */}
      <Card sx={{ p: 3, mb: 4, bgcolor: 'background.paper', overflow: 'visible' }}>
        <Box sx={{ display: 'flex', gap: 2, mb: showFilters ? 3 : 0, flexWrap: 'wrap' }}>
          <TextField
            fullWidth
            placeholder="Search details..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            onClick={() => setShowFilters(!showFilters)}
            startIcon={<Filter size={18} />}
            sx={{ borderColor: 'divider', color: 'text.primary' }}
          >
            Filters
          </Button>
          <ClickAwayListener onClickAway={() => setShowExportOptions(false)}>
            <Box sx={{ position: 'relative' }}>
              <Button
                variant="contained"
                onClick={() => setShowExportOptions(!showExportOptions)}
                startIcon={<Download size={18} />}
              >
                Export
              </Button>
              {showExportOptions && (
                <Paper sx={{ position: 'absolute', right: 0, top: '100%', mt: 1, zIndex: 50, boxShadow: 3, minWidth: '150px' }}>
                  <MenuItem onClick={() => { exportToCSV(); setShowExportOptions(false); }}>Export as CSV</MenuItem>
                  <MenuItem onClick={() => { exportToExcel(); setShowExportOptions(false); }}>Export as Excel</MenuItem>
                </Paper>
              )}
            </Box>
          </ClickAwayListener>
        </Box>

        <Collapse in={showFilters}>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Occupation</InputLabel>
                <Select value={filters.occupation} label="Occupation" onChange={(e) => handleFilterChange("occupation", e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {occupationOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            {filters.occupation === "Company Employee" && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Division</InputLabel>
                    <Select value={filters.division} label="Division" onChange={(e) => handleFilterChange("division", e.target.value)}>
                      <MenuItem value="">All</MenuItem>
                      {divisionOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Course Status</InputLabel>
                <Select value={filters.courseStatus} label="Course Status" onChange={(e) => handleFilterChange("courseStatus", e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {courseStatusOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Certificate</InputLabel>
                <Select value={filters.certificateStatus} label="Certificate" onChange={(e) => handleFilterChange("certificateStatus", e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {certificateStatusOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Collapse>
      </Card>

      {/* Table */}
      <Card sx={{ bgcolor: 'background.paper', overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                {fieldsToShow.map((field) => (
                  <TableCell key={field} sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {field}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {currentRows.map((row, idx) => (
                <TableRow
                  key={idx}
                  hover
                  onClick={() => { setSelectedUser(row); setShowCourseModal(true); }}
                  sx={{ cursor: 'pointer' }}
                >
                  {fieldsToShow.map((field) => (
                    <TableCell key={field}>
                      {field === "Status" ? (
                        <Box sx={{
                          px: 1.5, py: 0.5, borderRadius: 10, fontSize: '0.75rem', fontWeight: 600, display: 'inline-block',
                          bgcolor: row[field] === 'Active' ? '#dcfce7' : '#fee2e2',
                          color: row[field] === 'Active' ? '#166534' : '#991b1b'
                        }}>
                          {row[field]}
                        </Box>
                      ) : row[field]}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        <TablePagination
          component="div"
          count={filteredData.length}
          page={currentPage}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Detail Modal */}
      <Modal open={showCourseModal} onClose={() => setShowCourseModal(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: { xs: '90%', md: 800 }, bgcolor: 'background.paper', borderRadius: 3, boxShadow: 24, p: 4,
          maxHeight: '90vh', overflowY: 'auto'
        }}>
          {selectedUser && (
            <>
              <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>User Details: {selectedUser["First Name"]} {selectedUser["Last Name"]}</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">Contact Information</Typography>
                    <Typography variant="body1"><strong>Email:</strong> {selectedUser.Email}</Typography>
                    <Typography variant="body1"><strong>Mobile:</strong> {selectedUser["Mobile Number"]}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" color="text.secondary">Employment/Education</Typography>
                    <Typography variant="body1"><strong>Occupation:</strong> {selectedUser.Occupation}</Typography>
                    <Typography variant="body1"><strong>Edu:</strong> {selectedUser.Education}</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>Courses Enrolled</Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2">{selectedUser["Course (Enrolled)"]}</Typography>
                  </Paper>
                </Grid>
              </Grid>
              <Box sx={{ mt: 4, textAlign: 'right' }}>
                <Button variant="contained" onClick={() => setShowCourseModal(false)}>Close Details</Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default Dashboard;
